import { useCallback, useRef } from "react";
import type { TouchEvent, WheelEvent } from "react";
import { useDispatch } from "react-redux";

import { setScrollUp, setScrollDown } from "../app/actions/layout/scroll";

/** 方向判定の最小移動量(px)。これ未満の微小なスクロールは無視する。 */
const DEFAULT_THRESHOLD = 5;

/**
 * 内側スクロールコンテナ（`overflow-y: auto`）の wheel / touchmove から
 * スクロール方向を判定し、`state.layout`（"up" / "down"）へ dispatch する共有フック。
 *
 * GlobalWrapper は window レベルで方向を検出しているが、実スクロールが内側コンテナで
 * 完結する画面（DM の MessengerContent / オファーウィザードの OverlayBody など）では、
 * 特に SP で取りこぼしやすい。そのフェイルセーフとして、対象コンテナに直接ハンドラを
 * 貼ってユーザー操作起因の方向を判定し、BottomAppBar 等の表示/非表示と連動させる。
 *
 * - reducer は単純な値セットで idempotent。同値の連続 dispatch でも再レンダは増えない。
 * - wheel / touchmove のユーザー操作のみを見るため、`scrollTo` 等のプログラム的
 *   スクロールで誤発火しない。
 *
 * 使い方:
 * ```tsx
 * const scrollSync = useScrollDirectionLayoutSync();
 * return <div {...scrollSync}>...</div>;
 * ```
 */
export const useScrollDirectionLayoutSync = <
  T extends HTMLElement = HTMLDivElement,
>(
  threshold: number = DEFAULT_THRESHOLD,
) => {
  const dispatch = useDispatch();
  const touchStartYRef = useRef<number | null>(null);

  const onWheel = useCallback(
    (e: WheelEvent<T>) => {
      if (Math.abs(e.deltaY) < threshold) return;
      dispatch(e.deltaY > 0 ? setScrollDown() : setScrollUp());
    },
    [dispatch, threshold],
  );

  const onTouchStart = useCallback((e: TouchEvent<T>) => {
    touchStartYRef.current = e.touches[0]?.clientY ?? null;
  }, []);

  const onTouchMove = useCallback(
    (e: TouchEvent<T>) => {
      const startY = touchStartYRef.current;
      if (startY === null) return;
      const currentY = e.touches[0]?.clientY;
      if (currentY === undefined) return;
      // 指が上に動くと正（= 画面を下方向にスクロール）。
      const delta = startY - currentY;
      if (Math.abs(delta) < threshold) return;
      dispatch(delta > 0 ? setScrollDown() : setScrollUp());
      touchStartYRef.current = currentY;
    },
    [dispatch, threshold],
  );

  return { onWheel, onTouchStart, onTouchMove };
};

export default useScrollDirectionLayoutSync;
